import React from 'react';
import { AlertCircle, ChevronDown } from 'lucide-react';

/**
 * Form primitives for the coupon modal.
 *
 * The old form put validation errors in an `OverlayTrigger` tooltip anchored to a red dot, so the
 * message only existed on hover — invisible on touch, and gone the moment you moved to fix the
 * field. These render the message inline under the control instead, and swap the border colour so
 * the field itself reads as wrong at a glance.
 */

export const Field = ({ id, label, required, hint, error, action, children }) => (
    <div className="cpn-flex cpn-flex-col cpn-gap-1.5">
        <div className="cpn-flex cpn-items-center cpn-justify-between cpn-gap-2">
            <label htmlFor={id} className="cpn-text-sm cpn-font-semibold cpn-text-neutral-800">
                {label}
                {required && (
                    <span className="cpn-ml-0.5 cpn-text-brand-600" aria-hidden="true">
                        *
                    </span>
                )}
                {required && <span className="cpn-sr-only"> (required)</span>}
            </label>
            {action}
        </div>
        {children}
        {error ? (
            <p
                className="cpn-flex cpn-items-center cpn-gap-1.5 cpn-text-xs cpn-font-medium cpn-text-red-600"
                role="alert"
            >
                <AlertCircle className="cpn-h-3.5 cpn-w-3.5 cpn-shrink-0" aria-hidden="true" />
                {error}
            </p>
        ) : (
            hint && <p className="cpn-text-xs cpn-text-neutral-500">{hint}</p>
        )}
    </div>
);

export const inputClass =
    'cpn-h-11 cpn-w-full cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-px-3.5 cpn-text-sm cpn-text-neutral-900 placeholder:cpn-text-neutral-400 cpn-transition focus:cpn-border-brand-500 focus:cpn-outline-none focus:cpn-ring-4 focus:cpn-ring-brand-100';

export const errorInputClass =
    'cpn-h-11 cpn-w-full cpn-rounded-xl cpn-border cpn-border-red-300 cpn-bg-red-50/40 cpn-px-3.5 cpn-text-sm cpn-text-neutral-900 placeholder:cpn-text-neutral-400 cpn-transition focus:cpn-border-red-500 focus:cpn-outline-none focus:cpn-ring-4 focus:cpn-ring-red-100';

/** A native select, for the short fixed lists. Longer, data-driven lists use react-select. */
export const Select = ({ placeholder, options, ...props }) => (
    <div className="cpn-relative">
        <select {...props} className={`${inputClass} cpn-appearance-none cpn-pr-10`}>
            <option value="">{placeholder}</option>
            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
        <ChevronDown
            className="cpn-pointer-events-none cpn-absolute cpn-right-3.5 cpn-top-1/2 cpn-h-4 cpn-w-4 -cpn-translate-y-1/2 cpn-text-neutral-400"
            aria-hidden="true"
        />
    </div>
);

export const FormSection = ({ title, description, children }) => (
    <section className="cpn-rounded-2xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-p-5">
        <h3 className="cpn-text-sm cpn-font-bold cpn-text-neutral-900">{title}</h3>
        <p className="cpn-mt-0.5 cpn-text-xs cpn-text-neutral-500">{description}</p>
        <div className="cpn-mt-4 cpn-grid cpn-gap-4">{children}</div>
    </section>
);

export const ToggleRow = ({ id, label, description, checked, onChange, disabled }) => (
    <label
        htmlFor={id}
        className={`cpn-flex cpn-items-start cpn-gap-3 cpn-rounded-xl cpn-border cpn-border-neutral-200 cpn-bg-white cpn-p-3.5 cpn-transition ${
            disabled ? 'cpn-opacity-60' : 'cpn-cursor-pointer hover:cpn-border-brand-300'
        }`}
    >
        <input
            id={id}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={event => onChange(event.target.checked)}
            className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-rounded cpn-border-neutral-300 cpn-text-brand-700 focus:cpn-ring-brand-300"
        />
        <span>
            <span className="cpn-block cpn-text-sm cpn-font-semibold cpn-text-neutral-800">{label}</span>
            <span className="cpn-mt-0.5 cpn-block cpn-text-xs cpn-text-neutral-500">{description}</span>
        </span>
    </label>
);

export const RadioCard = ({ name, value, checked, label, description, onChange, disabled }) => (
    <label
        className={`cpn-flex cpn-items-start cpn-gap-3 cpn-rounded-xl cpn-border cpn-p-3.5 cpn-transition ${
            disabled ? 'cpn-opacity-60' : 'cpn-cursor-pointer'
        } ${
            checked
                ? 'cpn-border-brand-500 cpn-bg-brand-50/60 cpn-ring-2 cpn-ring-brand-100'
                : 'cpn-border-neutral-200 cpn-bg-white hover:cpn-border-brand-300'
        }`}
    >
        <input
            type="radio"
            name={name}
            value={value}
            checked={checked}
            disabled={disabled}
            onChange={() => onChange(value)}
            className="cpn-mt-0.5 cpn-h-4 cpn-w-4 cpn-border-neutral-300 cpn-text-brand-700 focus:cpn-ring-brand-300"
        />
        <span>
            <span className="cpn-block cpn-text-sm cpn-font-semibold cpn-text-neutral-800">{label}</span>
            <span className="cpn-mt-0.5 cpn-block cpn-text-xs cpn-text-neutral-500">{description}</span>
        </span>
    </label>
);

/** Read-only presentation of a field's value, for the view mode of the coupon modal. */
export const ReadOnlyValue = ({ children }) => (
    <p className="cpn-m-0 cpn-flex cpn-min-h-[2.75rem] cpn-items-center cpn-rounded-xl cpn-bg-neutral-50 cpn-px-3.5 cpn-text-sm cpn-text-neutral-800">
        {children}
    </p>
);
